import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { SLA_LATE } from '@/pages/RoStatusDashboard/buckets'

import {
  AGING_OUT_SOON_DAYS,
  DUE_SOON_HOURS,
  GUIDELINE_HOURS,
  QUEUE_GROUPS,
  queueById,
  QUEUES,
  SLA_HOURS,
} from './queue'
import {
  formatDollars,
  GUIDELINE,
  idleNoteFor,
  KPI_CARD,
  KPI_LINK,
  KpiGroup,
  KpiRow,
  QueueTile,
  shortLabel,
  Swatch,
} from './QueueTile'

/**
 * One Urgency card, in the queue tiles' style: a swatch for how hard the deadline is, the
 * count and its claim dollars, then where those ROs are. A card with one destination is
 * a link; a card that spans two tabs links its parts instead, as pills.
 */
const UrgencyCard = ({ amount, label, loading, note, parts, tone, to, value }) => {
  const body = (
    <>
      <span className="text-charcoal-700 flex flex-1 items-start text-[11px] font-bold uppercase tracking-widest">
        <Swatch color={tone.swatch} />
        {label}
      </span>
      <span className="text-charcoal-900 flex flex-wrap items-baseline gap-x-2 text-2xl font-semibold tabular-nums">
        {loading ? '—' : value.toLocaleString()}
        {/* Smaller than the count: "You don't have to make it in big blinky letters, but
            at least you could have the dollars" (Mark, 2026-10-08). */}
        {!loading && amount !== null && value > 0 && (
          <span className="text-charcoal-500 text-xs font-semibold">{formatDollars(amount)}</span>
        )}
      </span>
      <span className="flex min-h-[1.125rem] flex-wrap items-center gap-1.5">
        {!loading && value === 0 && <span className="text-charcoal-400 text-[11px]">None</span>}
        {!loading &&
          value > 0 &&
          parts
            .filter((part) => part.value > 0)
            .map((part) => (
              <Link
                className="inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-bold tabular-nums tracking-normal hover:underline"
                key={part.label}
                style={{ backgroundColor: tone.fill, color: tone.ink }}
                to={part.to}
              >
                {part.value.toLocaleString()} {part.label.toLowerCase()}
              </Link>
            ))}
        {!loading && value > 0 && note && (
          <span className="text-charcoal-400 text-[11px]">{note}</span>
        )}
      </span>
    </>
  )

  if (to) {
    return (
      <Link className={mergeClassNames(KPI_CARD, KPI_LINK)} to={to}>
        {body}
      </Link>
    )
  }
  return <div className={KPI_CARD}>{body}</div>
}

UrgencyCard.propTypes = {
  // The claim dollars on these ROs, or null for a card with no dollars.
  amount: PropTypes.number,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  note: PropTypes.string,
  parts: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, to: PropTypes.string, value: PropTypes.number }),
  ),
  tone: PropTypes.object.isRequired,
  to: PropTypes.string,
  value: PropTypes.number.isRequired,
}

UrgencyCard.defaultProps = { amount: null, note: null, parts: [], to: null }

/**
 * The line above the tiles: "196 to work · 80 late". Late is WPC's alone, as the flags
 * are. The same on the all-dealers dashboard and one dealer's, so the two headers read
 * alike (2026-10-12).
 */
export const WorkSummary = ({ counts, loading, showLate }) => {
  // Held at a line's height while loading, so the tiles do not jump.
  if (loading || !counts) return <p className="min-h-[1.75rem]" />

  const total = counts.total || 0
  const late = counts.late || 0
  return (
    <p className="text-charcoal-900 text-lg font-semibold tabular-nums">
      {total === 0 ? 'Nothing to work' : `${total.toLocaleString()} to work`}
      {showLate && late > 0 && (
        <span className="ml-1" style={{ color: SLA_LATE.ink }}>
          · {late.toLocaleString()} late
        </span>
      )}
    </p>
  )
}

WorkSummary.propTypes = {
  counts: PropTypes.object,
  loading: PropTypes.bool,
  showLate: PropTypes.bool,
}

WorkSummary.defaultProps = { counts: null, loading: false, showLate: false }

/**
 * The queue tiles in the Work Queue's two groups, then WPC's Urgency cards: the
 * contract's 24 hours to work a received RO, Mark's 48 hours with no word, and the
 * aging-out deadlines (WARP-960, WARP-965). Shared by the all-dealers dashboard and one
 * dealer's dashboard, which pass the counts they cover.
 *
 * `linkFor(queue, filter)` says where a tile or pill opens: that queue's Work Queue tab,
 * with `filter` (`late`, `waiting`, …) on, or null for none.
 */
const QueueKpiRow = ({ counts, linkFor, loading, showWpcSignals }) => {
  const queueCounts = counts?.queues || {}
  const reviews = ['admin-review', 'dealer-review'].map(queueById)
  const sideCounts = (queue) => queueCounts[queue.countsKey] || {}
  const across = (field) =>
    reviews.reduce((sum, queue) => sum + (sideCounts(queue)[field] || 0), 0)
  // The two Outstanding List sides, as pills that open each one's tab with the card's
  // filter on (WARP-965: both reviews, not the dealer's alone).
  const partsFor = (field) =>
    reviews.map((queue) => ({
      label: shortLabel(queue),
      to: linkFor(queue, field),
      value: sideCounts(queue)[field] || 0,
    }))
  const dueSoon = (queueCounts.ready?.dueSoon || 0) + (queueCounts.closed?.dueSoon || 0)

  return (
    <KpiRow>
      {QUEUE_GROUPS.map((group) => {
        const groupQueues = QUEUES.filter((queue) => queue.group === group.id)
        return (
          <KpiGroup key={group.id} label={group.label} tiles={groupQueues.length}>
            {groupQueues.map((queue) => (
              <QueueTile
                counts={queueCounts[queue.countsKey]}
                idleNote={idleNoteFor(queue)}
                key={queue.id}
                loading={loading}
                queue={queue}
                showFlag={showWpcSignals}
                to={linkFor(queue, null)}
              />
            ))}
          </KpiGroup>
        )
      })}

      {showWpcSignals && (
        <KpiGroup label="Urgency" tiles={4}>
          <UrgencyCard
            label={`Past ${SLA_HOURS}-hour SLA`}
            loading={loading}
            note={dueSoon ? `${dueSoon.toLocaleString()} due in ${DUE_SOON_HOURS} h` : null}
            parts={['ready', 'closed'].map((id) => ({
              label: shortLabel(queueById(id)),
              to: linkFor(queueById(id), 'late'),
              value: queueCounts[queueById(id).countsKey]?.late || 0,
            }))}
            tone={SLA_LATE}
            value={counts?.late || 0}
          />
          <UrgencyCard
            amount={across('waitingAmount')}
            label={`Over ${GUIDELINE_HOURS} hours`}
            loading={loading}
            parts={partsFor('waiting')}
            tone={GUIDELINE}
            value={across('waiting')}
          />
          <UrgencyCard
            amount={across('agingOutSoonAmount')}
            label={`Aging out in ${AGING_OUT_SOON_DAYS} days`}
            loading={loading}
            parts={partsFor('agingOutSoon')}
            tone={GUIDELINE}
            value={across('agingOutSoon')}
          />
          <UrgencyCard
            amount={across('agedOutAmount')}
            label="Aged out"
            loading={loading}
            parts={partsFor('agedOut')}
            tone={SLA_LATE}
            value={across('agedOut')}
          />
        </KpiGroup>
      )}
    </KpiRow>
  )
}

QueueKpiRow.propTypes = {
  // The work queue counts, camelCased: `{ total, late, queues: { ready, … } }`. Null
  // while loading.
  counts: PropTypes.object,
  linkFor: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  // The late, 48-hour, and aging flags and the Urgency cards are WPC's alone.
  showWpcSignals: PropTypes.bool,
}

QueueKpiRow.defaultProps = {
  counts: null,
  loading: false,
  showWpcSignals: false,
}

export default QueueKpiRow
